/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: ChangePasswordBox.tsx
 🧩 Description:
    Self-contained Change Password FormBox for the User Profile page.
    Manages its own state and calls PUT /api/auth/change-password.
────────────────────────────────────────────────────────────────*/

import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useNotifications } from '../../../hooks/useNotifications';
import api from '../../../lib/api';

/* ─── Shared styles (mirrors UserProfileFormFields for consistency) ─── */
const inp = 'px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full';

/* ─── Strength helpers ─────────────────────────────────────── */
function strengthLevel(pw: string) {
  return pw.length >= 12 ? 4 : pw.length >= 10 ? 3 : pw.length >= 8 ? 2 : 1;
}
function strengthColor(pw: string) {
  return pw.length >= 12
    ? 'bg-green-500'
    : pw.length >= 10
    ? 'bg-yellow-500'
    : 'bg-orange-500';
}

/* ─── Component ─────────────────────────────────────────────── */
export function ChangePasswordBox() {
  const { showSuccess, showError } = useNotifications();

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  const set = (f: Partial<typeof form>) => setForm(prev => ({ ...prev, ...f }));

  const hasChanges = !!(form.currentPassword || form.newPassword || form.confirmPassword);

  const handleSave = async () => {
    setError('');
    if (!form.currentPassword)      { setError('Current password is required.');       return; }
    if (!form.newPassword)           { setError('New password is required.');           return; }
    if (form.newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (form.newPassword !== form.confirmPassword) { setError('New passwords do not match.'); return; }

    setSaving(true);
    try {
      await api.put('/api/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword:     form.newPassword,
      });
      showSuccess('Password changed successfully');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to change password';
      setError(msg);
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormBox title="Change Password" icon={<Lock />}>
      {/* Error banner */}
      {error && (
        <div className="mb-4 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Row 1 — Current Password */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center mb-4">
        <SimpleTooltip content="Your existing password. Required to authorize any password change." position="top">
          <label className="text-sm font-medium text-foreground cursor-default">Current Password</label>
        </SimpleTooltip>
        <input
          type="password"
          value={form.currentPassword}
          onChange={e => set({ currentPassword: e.target.value })}
          placeholder="Enter current password"
          className={inp}
          autoComplete="current-password"
        />
        <div /><div />
      </div>

      {/* Divider */}
      <div className="border-t border-border/50 mb-4" />

      {/* Row 2 — New + Confirm */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center">
        <SimpleTooltip content="Must be at least 8 characters. Longer is stronger." position="top">
          <label className="text-sm font-medium text-foreground cursor-default">New Password</label>
        </SimpleTooltip>
        <div className="space-y-1">
          <input
            type="password"
            value={form.newPassword}
            onChange={e => set({ newPassword: e.target.value })}
            placeholder="Min. 8 characters"
            className={inp}
            autoComplete="new-password"
          />
          {form.newPassword && (
            <div className="flex gap-1">
              {[1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= strengthLevel(form.newPassword)
                      ? strengthColor(form.newPassword)
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <SimpleTooltip content="Repeat the new password exactly to confirm the change." position="top">
          <label className="text-sm font-medium text-foreground cursor-default">Confirm Password</label>
        </SimpleTooltip>
        <div className="space-y-1">
          <input
            type="password"
            value={form.confirmPassword}
            onChange={e => set({ confirmPassword: e.target.value })}
            placeholder="Repeat new password"
            className={`${inp} ${
              form.confirmPassword && form.newPassword !== form.confirmPassword
                ? 'border-destructive ring-1 ring-destructive/50'
                : form.confirmPassword && form.newPassword === form.confirmPassword
                ? 'border-green-500 ring-1 ring-green-500/30'
                : ''
            }`}
            autoComplete="new-password"
          />
          {form.confirmPassword && (
            <p className={`text-xs ${form.newPassword === form.confirmPassword ? 'text-green-500' : 'text-destructive'}`}>
              {form.newPassword === form.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
            </p>
          )}
        </div>
      </div>

      {/* Save button — only visible when any field has a value */}
      {hasChanges && (
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Lock className="w-4 h-4" />
            }
            {saving ? 'Saving…' : 'Change Password'}
          </button>
        </div>
      )}
    </FormBox>
  );
}
